import { Injectable } from "@angular/core";
import { AlertController, Platform } from "@ionic/angular";
import { AuthService } from "./auth.service";
import { LangsRepository } from "./langs.repository";
import { environment } from "../../environments/environment";

/** Same endpoints as sessionHeartbeatClient.js and sessionHeartbeat.ts: /api/session/config, authAuthority/api/access/heartbeat. Heartbeat disabled for now - only inactivity logout. */
interface SessionConfig {
    authAuthority: string;
    inactivityMinutes?: number;
    heartbeatIntervalMinutes?: number;
    warnBeforeExpiryMinutes?: number;
    logoutUrl?: string;
}

const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];
/** Check inactivity every 30 seconds so we detect timeout soon after it elapses. */
const INACTIVITY_CHECK_INTERVAL_MS = 30 * 1000;

@Injectable()
export class SessionHeartbeatService {
    private lastActivity = Date.now();
    // private warningShown = false; // Unused when warning popup is commented out
    private timer: number | null = null;
    private expiring = false;
    private started = false;
    private cfg: SessionConfig | null = null;
    private baseUrl: string = "";

    constructor(
        private authService: AuthService,
        private platform: Platform,
        // private alertController: AlertController,
        // private langsRepository: LangsRepository
    ) {
        ACTIVITY_EVENTS.forEach((evt) =>
            document.addEventListener(
                evt,
                () => {
                    this.lastActivity = Date.now();
                    // this.warningShown = false; // Unused when warning popup is commented out
                },
                { passive: true }
            )
        );
    }

    async start(): Promise<void> {
        if (this.started || !this.authService.authenticated) return;
        this.started = true;

        const cfg = await this.loadConfig();
        if (!cfg || !cfg.authAuthority) return;

        this.cfg = cfg;
        this.baseUrl = (environment.baseFixUrl || "").replace(/\/+$/, "");

        this.checkInactivity();
        this.timer = window.setInterval(() => this.checkInactivity(), INACTIVITY_CHECK_INTERVAL_MS);
        // Heartbeat commented out for now - uncomment ping() and use it instead of checkInactivity to re-enable
    }

    stop(): void {
        if (this.timer != null) {
            window.clearInterval(this.timer);
            this.timer = null;
        }
        this.started = false;
    }

    private loadConfig(): Promise<SessionConfig | null> {
        const baseUrl = (environment.baseFixUrl || "").replace(/\/+$/, "");
        const url = baseUrl ? `${baseUrl}/api/session/config` : "";
        if (!url) return Promise.resolve(null);
        return fetch(url, { method: "GET", credentials: "include" })
            .then((res) => (res.ok ? res.json() : null))
            .catch(() => null);
    }

    private getLogoutRedirectUrl(): Promise<string | null> {
        const logoutUrl = this.cfg && this.cfg.logoutUrl ? this.cfg.logoutUrl : null;
        if (!logoutUrl) return Promise.resolve(null);
        const fullUrl = logoutUrl.startsWith("http") ? logoutUrl : `${this.baseUrl}${logoutUrl}`;
        return fetch(fullUrl, { method: "GET", credentials: "include" })
            .then((res) => (res.ok ? res.json() : null))
            .then((data: { Success?: boolean; Data?: string }) =>
                data && data.Success && typeof data.Data === "string" ? data.Data : null
            )
            .catch(() => null);
    }

    private buildLoginUrl(): string {
        const authAuthority = (this.cfg && this.cfg.authAuthority) || "";
        const returnUrl = window.location.href;
        return `${authAuthority}/Login?returnUrl=${encodeURIComponent(returnUrl)}`;
    }

    private checkInactivity(): void {
        if (!this.cfg || !this.authService.authenticated || this.expiring) return;
        const inactivityMs = (this.cfg.inactivityMinutes != null ? this.cfg.inactivityMinutes : 30) * 60 * 1000;
        const idleMs = Date.now() - this.lastActivity;
        if (idleMs >= inactivityMs) {
            this.expiring = true;
            this.stop();
            if (this.platform.is("cordova")) {
                // The web flow below sends the app's own WebView to the auth site, and auth answers the
                // ionic:// return URL with the web build of this app - the user is left in it inside the
                // native shell, where no plugin works (#370).
                this.authService.logoutAndRedirect();
                return;
            }
            this.getLogoutRedirectUrl().then((redirectUrl) => {
                const url = redirectUrl || this.buildLoginUrl();
                this.authService.logout();
                window.location.href = url;
            });
        }
    }

    // Heartbeat commented out for now - uncomment to re-enable:
    // private ping(): Promise<void> {
    //     if (!this.cfg || !this.authService.authenticated) {
    //         if (!this.authService.authenticated) this.stop();
    //         return Promise.resolve();
    //     }
    //     const heartbeatUrl = `${this.cfg.authAuthority}/api/access/heartbeat`;
    //     return fetch(heartbeatUrl, {
    //         method: "GET",
    //         headers: { "X-Heartbeat": "true" },
    //         credentials: "include",
    //     })
    //         .then((res) => {
    //             if (res.status === 401) {
    //                 this.stop();
    //                 this.getLogoutRedirectUrl().then((redirectUrl) => {
    //                     const url = redirectUrl || this.buildLoginUrl();
    //                     this.authService.logout();
    //                     window.location.href = url;
    //                 });
    //                 return;
    //             }
    //             this.checkInactivity();
    //         })
    //         .catch(() => {});
    // }


    // private word(key: string): string {
    //     const cur = this.langsRepository.current;
    //     const words = cur && cur.words;
    //     return (words && words[key]) || key;
    // }

    // Commented out: show popup when session is about to end. Uncomment if needed later.
    // private async showWarning(secondsLeft: number): Promise<void> {
    //     const msg = this.word("SessionHeartbeat.WarningMessage").replace("{seconds}", String(secondsLeft));
    //     const alert = await this.alertController.create({
    //         header: this.word("SessionHeartbeat.WarningTitle"),
    //         message: msg,
    //         buttons: [
    //             {
    //                 text: this.word("SessionHeartbeat.Logout"),
    //                 role: "cancel",
    //                 handler: () => {
    //                     this.getLogoutRedirectUrl().then((redirectUrl) => {
    //                         window.location.href = redirectUrl || this.buildLoginUrl();
    //                     });
    //                 },
    //             },
    //             {
    //                 text: this.word("SessionHeartbeat.StayLoggedIn"),
    //                 handler: () => {
    //                     this.lastActivity = Date.now();
    //                     this.warningShown = false;
    //                     this.ping();
    //                 },
    //             },
    //         ],
    //     });
    //     await alert.present();
    // }

    // Commented out: show popup before logout. Uncomment if needed later.
    // private async showExpired(redirectUrl: string): Promise<void> {
    //     const alert = await this.alertController.create({
    //         header: this.word("SessionHeartbeat.ExpiredTitle"),
    //         message: this.word("SessionHeartbeat.ExpiredMessage"),
    //         backdropDismiss: false,
    //         buttons: [
    //             {
    //                 text: this.word("SessionHeartbeat.GoToLogin"),
    //                 handler: () => {
    //                     this.authService.logout();
    //                     window.location.href = redirectUrl;
    //                 },
    //             },
    //         ],
    //     });
    //     await alert.present();
    // }
}
